import * as React from 'react';
import { FC, memo, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import withAddIncentiveToCart from 'components/with-add-incentive-to-cart';
import { RedemptionMethod } from 'components/with-add-incentive-to-cart/types';
import { useFavoriteOffers } from 'hooks/favorite-offers';
import { useGetUrlSearchParam } from 'hooks/location/use-get-url-search-param';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { isLoyaltyOffer, isReward, isValidOfferIncentive } from 'state/loyalty/types';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';

import { parseIncentiveData } from '../incentive-card/parse-incentive-data';
import { InvalidServiceModeModal } from '../invalid-service-mode-modal';
import { useLoyaltyIncentivesAvailability } from '../use-loyalty-incentives-availability';

import { ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA } from './constants';
import { IncentiveDetailsView } from './incentive-details.view';
import { IIncentiveDetailsProps, LoyaltyRedemptionTypes } from './types';

export const MOBILE_REDEMPTION: string[] = [
  LoyaltyRedemptionTypes.MOBILE_ONLY,
  LoyaltyRedemptionTypes.MOBILEANDRESTAURANT,
];
export const RESTAURANT_REDEMPTION: string[] = [
  LoyaltyRedemptionTypes.RESTAURANT_ONLY,
  LoyaltyRedemptionTypes.MOBILEANDRESTAURANT,
];

/**
 *
 * IncentiveDetails displays an expanded view of a incentive when selected from the incentive list
 *
 */
const IncentiveDetails: FC<IIncentiveDetailsProps> = ({
  selectedIncentive,
  engineIncentivesMap,
  addIncentiveToCart,
  challengeIncentiveSelected,
}) => {
  const incentiveLoyaltyEngineId = selectedIncentive?.loyaltyEngineId || '';
  const engineReward = engineIncentivesMap[incentiveLoyaltyEngineId];

  const {
    pointCost,
    locked,
    redemptionPercentage,
    remainingPointsNeededForRedemption,
    rewardBenefitId,
  } = engineReward || {};

  const mParticle = useMParticleContext();

  const { removeFromCart } = useOrderContext();
  const {
    inRestaurantLoyaltyEnabledAtRestaurant,
    clearInRestaurantRedemptionAllRewards,
    enableRewardRedemption,
  } = useInRestaurantRedemptionContext();
  const rewardData = parseIncentiveData(selectedIncentive);
  const { noStoreSelected } = useStoreContext();
  const { formatMessage } = useIntl();
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [showRemoveRewardButton, setShowRemoveRewardButton] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const enableMobileOffer = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_MOBILE);
  const enableSwitchServiceModeModal = useFlag(LaunchDarklyFlag.ENABLE_SWITCH_SERVICE_MODE_MODAL);

  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);

  const { incentiveId, incentiveType } =
    useMemo(() => {
      const incentive = isReward(selectedIncentive)
        ? selectedIncentive.incentives?.find(benefit => benefit?._id === rewardBenefitId)
        : selectedIncentive.incentives?.[0];
      return {
        incentiveId: isValidOfferIncentive(incentive) ? incentive?._id : '',
        incentiveType: incentive?._type,
      };
    }, [rewardBenefitId, selectedIncentive]) || {};
  const dispatch = useAppDispatch();
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);

  const { checkIncentiveAvailability, incentiveUnavailableMessage } =
    useLoyaltyIncentivesAvailability(selectedIncentive, pointCost, locked);
  const exceedsBalanceError = formatMessage({ id: 'exceedsRewardsBalance' });

  const { toggleFavorite, isFavorite, isOfferLoading } = useFavoriteOffers();

  // if the redemptionMethod is not defined set mobileAndRestaurant as default method
  const redemptionMethod =
    selectedIncentive?.redemptionMethod || LoyaltyRedemptionTypes.MOBILEANDRESTAURANT;

  useEffect(() => {
    if (incentiveUnavailableMessage) {
      setErrorMessage(incentiveUnavailableMessage);
      setShowRemoveRewardButton(incentiveUnavailableMessage === exceedsBalanceError);
      return;
    }
    if (noStoreSelected) {
      setErrorMessage('');
      return;
    }

    // Check incentive item availability in selected store
    (async () => {
      setErrorMessage('');
      setCheckingAvailability(true);
      try {
        const isAvailable =
          incentiveType !== 'offerDiscount'
            ? await checkIncentiveAvailability(`${incentiveType}-${incentiveId}`)
            : true;
        const storeError = isAvailable ? '' : formatMessage({ id: 'nonAvailableItemInStore' });
        setErrorMessage(storeError);
        setCheckingAvailability(false);
      } catch {
        setCheckingAvailability(false);
      }
    })();
  }, [
    incentiveId,
    incentiveType,
    checkIncentiveAvailability,
    exceedsBalanceError,
    formatMessage,
    noStoreSelected,
    incentiveUnavailableMessage,
  ]);

  const incentiveEvaluationResults = offersFeedbackMap[incentiveLoyaltyEngineId];
  const handleRemoveRewardsClick = () => {
    if (appliedLoyaltyRewardsArray?.length) {
      appliedLoyaltyRewardsArray.forEach(appliedReward => {
        removeFromCart(appliedReward);
        dispatch(
          actions.loyalty.removeAppliedReward({
            cartId: appliedReward.cartId,
            rewardBenefitId: appliedReward.rewardId,
          })
        );
      });
      clearInRestaurantRedemptionAllRewards();
      removeAppliedRewardsInStorage();

      setShowRemoveRewardButton(false);
    }
  };

  const showRedeemRewardInRestaurantCTAValue = useGetUrlSearchParam(
    ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA
  );
  const onlyShowRedeemRewardInRestaurantCTA = showRedeemRewardInRestaurantCTAValue === 'true';

  const isLoyaltyOrReward =
    isLoyaltyOffer(selectedIncentive) || (isReward(selectedIncentive) && enableRewardRedemption);

  if (isLoyaltyOffer(selectedIncentive)) {
    dispatch(actions.loyalty.setSelectedOffer(selectedIncentive));
  }

  const loyaltyInRestaurantRedemptionAvailable = RESTAURANT_REDEMPTION.includes(redemptionMethod);

  // only when Restaurant Redemption set in Sanity
  const canAddToInRestaurantOrder =
    inRestaurantLoyaltyEnabledAtRestaurant &&
    isLoyaltyOrReward &&
    !selectedIncentive.mobileOrderOnly &&
    loyaltyInRestaurantRedemptionAvailable;

  const loyaltyMobileRedemptionAvailable =
    enableMobileOffer && MOBILE_REDEMPTION.includes(redemptionMethod);

  const canAddToMobileOrder =
    !onlyShowRedeemRewardInRestaurantCTA && loyaltyMobileRedemptionAvailable && isLoyaltyOrReward;

  return (
    <>
      {enableSwitchServiceModeModal &&
        !checkingAvailability &&
        incentiveEvaluationResults?.length && (
          <InvalidServiceModeModal incentiveEvaluationResults={incentiveEvaluationResults} />
        )}
      <IncentiveDetailsView
        {...rewardData}
        pointCost={pointCost}
        locked={locked}
        redemptionPercentage={redemptionPercentage}
        handleRedeemRewardClick={() => {
          addIncentiveToCart({ incentive: selectedIncentive, method: RedemptionMethod.MOBILE });
        }}
        handleInRestaurantRedemptionClick={() => {
          addIncentiveToCart({
            incentive: selectedIncentive,
            method: RedemptionMethod.IN_RESTAURANT,
          });
          mParticle.logRBIEvent({
            name: isLoyaltyOffer(selectedIncentive)
              ? CustomEventNames.OFFER_ADDED_TO_ORDER
              : CustomEventNames.REWARD_ADDED_TO_ORDER,
            type: EventTypes.Other,
            attributes: {
              engineId: selectedIncentive.loyaltyEngineId ?? '',
              sanityId: selectedIncentive._id,
              name: isReward(selectedIncentive) ? selectedIncentive.name?.locale ?? '' : '',
              redemptionMode: 'In-Store',
            },
          });
        }}
        remainingPointsNeededForRedemption={remainingPointsNeededForRedemption}
        errorMessage={errorMessage}
        key={rewardData.id}
        checkingAvailability={checkingAvailability}
        canAddToInRestaurantOrder={canAddToInRestaurantOrder}
        canAddToMobileOrder={canAddToMobileOrder}
        showRemoveRewardButton={showRemoveRewardButton}
        handleRemoveRewardsClick={handleRemoveRewardsClick}
        incentiveEvaluationResults={incentiveEvaluationResults}
        offersInCooldown={loyaltyOffersInCooldown}
        favorited={enableFavoriteOffers && isFavorite(selectedIncentive.loyaltyEngineId || '')}
        favoriteLoading={isOfferLoading(selectedIncentive.loyaltyEngineId || '')}
        handleToggleFavorite={toggleFavorite}
        challengeIncentiveSelected={challengeIncentiveSelected}
      />
    </>
  );
};

export default memo(withAddIncentiveToCart(IncentiveDetails));
